import React, { useState, useEffect, useContext } from 'react';
import CustomModal from '../../revampComponents/Atom/Modals/CustomModal';
import FormButtons from '../../revampComponents/Molecule/Buttons/FormButtons';
import { editNotification } from '../../revampCRUD/organisers.crud';
import { errorToaster, successToaster } from '../../reusable/common';
import AuthContext from '../../contexts/AuthContext';
import ToolTip from '../../revampComponents/Atom/ToolTip';

const EmailNotification = ({
    emailNotification,
    setEmailNotification,
    notificationDetails,
    eventId,
    listOrganizerDetails
}) => {
    const { eventInfo: eventDetails } = useContext(AuthContext);
    const [checkboxes, setCheckboxes] = useState({});
    const [initialCheckboxes, setInitialCheckboxes] = useState({});
    const [selectAll, setSelectAll] = useState(false);
    const [loading, setLoading] = useState(false);

    const eventNotificationKeys = eventDetails?.is_free === false ? {
        notify_approval_requested: "Approval Request Emails",
        notify_approval_approved: "Approval Request Approved",
        notify_approval_rejected: "Approval Request Rejected",
        notify_waitlist_approved: "Waitlist Request Approved",
        notify_waitlist_rejected: "Waitlist Request Rejected",
        notify_ticket_expiry: "Ticket Expiry Notification"
    } : { notify_ticket_expiry: "Ticket Expiry Notification" };

    const keyDisplayNameMap = {
        notify_reg_cancel: "Attendee Cancellation",
        notify_reg_refund: "Attendee Refund Issuance",
        notify_reg_edit: "Attendee Details Update",
        notify_reg_transfer: "Attendee Registration Transfer",
        notify_referral_contest_created: "Referral Contest Creation",
        notify_referral_contest_edit: "Referral Contest Edit",
        notify_referral_contest_stop: "Referral Contest Stop",
        notify_referral_contest_delete: "Referral Contest Deletion",
        notify_event_organiser_added: "Organizing Team Changes",
        notify_new_ticket_sale: "New Ticket Sale Notification",
        notify_daily_ticket_sale_summary: "Daily Ticket Sale Summary",
        ...eventNotificationKeys
    };

    const toolTipMap = {
        notify_reg_cancel: "Receive an email when an attendee ticket is cancelled.",
        notify_reg_refund: "Receive an email when a refund is issued to an attendee.",
        notify_reg_edit: "Receive an email when an attendee's details are updated.",
        notify_reg_transfer: "Receive an email when an attendee registration is transferred to another person.",
        notify_referral_contest_created: "Receive an email when a new referral contest is created for your event.",
        notify_referral_contest_edit: "Receive an email when a referral contest is updated or edited.",
        notify_referral_contest_stop: "Receive an email when a referral contest is stopped or concluded.",
        notify_referral_contest_delete: "Receive an email when a referral contest is deleted from the event.",
        notify_event_organiser_added: "Receive an email when there are changes made to the organizing team.",
        notify_new_ticket_sale: "Receive an email every time a new ticket is sold for the event.",
        notify_daily_ticket_sale_summary: "Receive a daily summary of ticket sales for your event.",
        notify_approval_requested: "Receive an email when a new request for approval is submitted.",
        notify_approval_approved: "Receive an email when a request for approval is approved.",
        notify_approval_rejected: "Receive an email when a request for approval is rejected.",
        notify_waitlist_approved: "Receive an email when a waitlisted attendee is approved for the event.",
        notify_waitlist_rejected: "Receive an email when a waitlisted attendee's request is rejected.",
        notify_ticket_expiry: "Receive an email when a ticket is about to expire."
    };

    useEffect(() => {
        const initialCheckboxesState = {};
        Object.keys(notificationDetails).forEach(key => {
            if (typeof notificationDetails[key] === 'boolean') {
                initialCheckboxesState[key] = notificationDetails[key];
            }
        });
        setCheckboxes(initialCheckboxesState);
        setInitialCheckboxes(initialCheckboxesState);
    }, [notificationDetails]);


    useEffect(() => {
        // Check if all checkboxes are selected
        const allChecked = Object.values(checkboxes).every(value => value === true);
        setSelectAll(allChecked);
    }, [checkboxes]);

    const handleCheckboxChange = (key) => {
        setCheckboxes(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleSelectAllChange = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        const newCheckboxes = {};
        Object.keys(checkboxes).forEach(key => {
            newCheckboxes[key] = newSelectAll;
        });
        setCheckboxes(newCheckboxes);
    };

    const handleSave = async () => {
        const payload = {};
        Object.keys(checkboxes).forEach(key => {
            if (checkboxes[key] !== initialCheckboxes[key]) {
                payload[key] = checkboxes[key];
            }
        });

        setLoading(true);
        try {
            const response = await editNotification(eventId, payload);
            if (response.status === 200) {
                successToaster(response.data);
                setEmailNotification(false);
                listOrganizerDetails();
            }
        } catch (err) {
            const error = err?.response?.data?.error || "Something went wrong!";
            errorToaster(error);
            setEmailNotification(false);
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setEmailNotification(false);
    };

    const isChanged = JSON.stringify(checkboxes) !== JSON.stringify(initialCheckboxes);

    const firstColumnKeys = Object.keys(keyDisplayNameMap).slice(0, 8);
    const secondColumnKeys = Object.keys(keyDisplayNameMap).slice(8);

    return (
        <>
            <CustomModal
                width="50%"  // Increased width as requested
                header={"Configure email notification"}
                subHeader={"Receive email notifications for the following:"}
                openModal={emailNotification}
                setOpenModal={() => setEmailNotification(false)}
                overflow={"scroll"}
                saveBtnContent={null}
                content={
                    <div className="email-notification-container">
                        <div className="mb-3">
                            <label className="label">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                />
                                Select All
                            </label>
                        </div>
                        <div className="checkbox-columns">
                            <div className="column">
                                {firstColumnKeys.map(key => (
                                    <label key={key} className="label checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={checkboxes[key]}
                                            onChange={() => handleCheckboxChange(key)}
                                        />
                                        {keyDisplayNameMap[key]}
                                        <ToolTip data={toolTipMap[key]} isEmailTooltip={true} />
                                    </label>
                                ))}
                            </div>
                            <div className="column">
                                {secondColumnKeys.map(key => (
                                    <label key={key} className="label checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={checkboxes[key]}
                                            onChange={() => handleCheckboxChange(key)}
                                        />
                                        {keyDisplayNameMap[key]}
                                        <ToolTip data={toolTipMap[key]} isEmailTooltip={true} />
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="mt-2">
                            <label className="label">
                                <b>Note</b>: Email Campaign launch notifications will be sent only to organizers who initiate or schedule the campaign.
                            </label>
                        </div>
                        <FormButtons
                            onSave={handleSave}
                            onCancel={handleCancel}
                            saveBtnContent="&nbsp; Save &nbsp;"
                            isLoading={loading}
                            saveBtnDisabled={!isChanged || loading}
                        />
                    </div>
                }
            />
            <style>{`
                .email-notification-container {
                    padding: 15px;
                }
                .checkbox-columns {
                    display: flex;
                    justify-content: space-between;
                    gap: 15px; /* This ensures equal gap between columns */
                    border: 1px solid #C3CDDF;
                    padding: 15px;
                    border-radius: 5px;
                }
        
                .column {
                    flex: 1;
                }
        
                .checkbox-label {
                    display: block;
                    margin-bottom: 10px;
                }
            `}</style>
        </>
    );
};

export default EmailNotification;
